import { format } from 'date-fns'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { KeyboardEvent, useState } from 'react'
import { Button, Card, Form, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { documentNotesService } from '../../services/document-notes.service'
import { toastService } from '../../services/toast.service'
import { DocumentNote } from '../../types'
import './DocumentNotes.scss'

function displayNameFromNoteUser(user: DocumentNote['user']): string {
  if (user == null || typeof user === 'number') {
    return ''
  }
  const nameComponents: string[] = []
  if (user.first_name) nameComponents.push(user.first_name)
  if (user.last_name) nameComponents.push(user.last_name)
  if (user.username) {
    if (nameComponents.length > 0) nameComponents.push(`(${user.username})`)
    else nameComponents.push(user.username)
  }
  return nameComponents.join(' ')
}

interface DocumentNotesProps {
  documentId: number
  addDisabled?: boolean
  deleteDisabled?: boolean
  editDisabled?: boolean
}

const DocumentNotes: React.FC<DocumentNotesProps> = ({
  documentId,
  addDisabled = false,
  deleteDisabled = false,
  editDisabled = false,
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [newNote, setNewNote] = useState('')
  const [newNoteError, setNewNoteError] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const { data: notes = [] } = useQuery<DocumentNote[]>({
    queryKey: ['document-notes', documentId],
    queryFn: () => documentNotesService.getNotes(documentId),
    enabled: Boolean(documentId),
  })

  const addNoteMutation = useMutation({
    mutationFn: (note: string) => documentNotesService.addNote(documentId, note),
    onSuccess: () => {
      setNewNote('')
      void queryClient.invalidateQueries({ queryKey: ['document-notes', documentId] })
      void queryClient.invalidateQueries({ queryKey: ['document-history', documentId] })
      toastService.showSuccess(t('note_added_successfully'))
    },
    onError: (error) => {
      toastService.showError(t('error_saving_note'), error)
    },
  })

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: number) => documentNotesService.deleteNote(documentId, noteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['document-notes', documentId] })
      void queryClient.invalidateQueries({ queryKey: ['document-history', documentId] })
      toastService.showSuccess(t('note_deleted_successfully'))
    },
    onError: (error) => {
      toastService.showError(t('error_deleting_note'), error)
    },
  })

  const updateNoteMutation = useMutation({
    mutationFn: ({ noteId, text }: { noteId: number; text: string }) =>
      documentNotesService.updateNote(documentId, noteId, text),
    onSuccess: () => {
      setEditingNoteId(null)
      setEditDraft('')
      void queryClient.invalidateQueries({ queryKey: ['document-notes', documentId] })
      void queryClient.invalidateQueries({ queryKey: ['document-history', documentId] })
      toastService.showSuccess(t('note_updated_successfully'))
    },
    onError: (error) => {
      toastService.showError(t('error_updating_note'), error)
    },
  })

  const networkActive = addNoteMutation.isPending || deleteNoteMutation.isPending || updateNoteMutation.isPending

  const addNote = () => {
    if (addDisabled) return
    const note = newNote.trim()
    if (note.length === 0) {
      setNewNoteError(true)
      return
    }
    setNewNoteError(false)
    addNoteMutation.mutate(note)
  }

  const deleteNote = (noteId: number) => {
    if (deleteDisabled) return
    deleteNoteMutation.mutate(noteId)
  }

  const startEdit = (note: DocumentNote) => {
    if (editDisabled) return
    setEditingNoteId(note.id)
    setEditDraft(note.note)
  }

  const cancelEdit = () => {
    setEditingNoteId(null)
    setEditDraft('')
  }

  const saveEdit = () => {
    if (editDisabled || editingNoteId == null) return
    const text = editDraft.trim()
    if (text.length === 0) {
      toastService.showError(t('note_cannot_be_empty'))
      return
    }
    updateNoteMutation.mutate({ noteId: editingNoteId, text })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (addDisabled) return
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      addNote()
    }
  }

  return (
    <div>
      {notes && (
        <>
          <Form className="needs-validation" noValidate>
            <Form.Group className="mb-2">
              <Form.Control
                as="textarea"
                rows={3}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('enter_note')}
                isInvalid={newNoteError}
                size="sm"
                required
                disabled={addDisabled}
              />
              <Form.Control.Feedback type="invalid">Please enter a note.</Form.Control.Feedback>
            </Form.Group>
            <div className="d-flex justify-content-end align-items-center mb-3">
              {networkActive && <Spinner animation="border" size="sm" className="fw-normal me-auto" role="status" />}
              <Button variant="primary" size="sm" disabled={networkActive || addDisabled} onClick={addNote}>
                {t('add_note')}
              </Button>
            </div>
          </Form>
          {notes.map((note) => (
            <Card key={note.id} className="border mb-3">
              <Card.Body>
                {editingNoteId === note.id ? (
                  <>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                        if (editDisabled) return
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                          e.preventDefault()
                          saveEdit()
                        }
                      }}
                      size="sm"
                      className="mb-2"
                      disabled={networkActive}
                    />
                    <div className="d-flex justify-content-end gap-2">
                      <Button variant="outline-secondary" size="sm" disabled={networkActive} onClick={cancelEdit}>
                        {t('cancel')}
                      </Button>
                      <Button variant="primary" size="sm" disabled={networkActive} onClick={saveEdit}>
                        {t('save_note')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <Card.Text style={{ whiteSpace: 'pre-wrap' }}>{note.note}</Card.Text>
                )}
              </Card.Body>
              <Card.Footer className="d-flex small text-muted justify-content-between align-items-center">
                <span>
                  {displayNameFromNoteUser(note.user)} - {format(new Date(note.created), 'dd.MM.yy.')}
                </span>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-secondary"
                    title={t('edit_note')}
                    disabled={editDisabled || editingNoteId !== null || networkActive}
                    onClick={() => startEdit(note)}
                  >
                    <i className="bi bi-pencil"></i>
                    <span className="visually-hidden">{t('edit_note')}</span>
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-danger"
                    title={t('delete_note')}
                    disabled={deleteDisabled || editingNoteId !== null || networkActive}
                    onClick={() => deleteNote(note.id)}
                  >
                    <i className="bi bi-trash"></i>
                    <span className="visually-hidden">Delete note</span>
                  </Button>
                </div>
              </Card.Footer>
            </Card>
          ))}
        </>
      )}
    </div>
  )
}

export default DocumentNotes
